<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-3xl mx-4 sm:mx-6">
  @if (vm; as u) {
    <!--
      `overflow-hidden` + `shrink-0` en cabecera/pestañas/pie y `min-h-0` en el cuerpo.
      Sin esto, el cuerpo (que en la pestaña «Línea de tiempo» crece con cada evento y con
      cada diff expandido) empuja el flex-shrink por defecto y APLASTA la barra de pestañas
      —que puede encogerse hasta 0 porque tiene `overflow-x-auto`—, recortando sus títulos.
      Mismo patrón que workflow-detail-modal / business-detail-modal.
    -->
    <div class="flex flex-col max-h-[85vh] overflow-hidden">
      <!-- Header -->
      <div class="shrink-0 flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-start gap-3 min-w-0">
          @if (u.avatarUrl) {
            <img [src]="u.avatarUrl" [alt]="displayName"
                 class="h-12 w-12 shrink-0 rounded-lg object-cover" />
          } @else {
            <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-lg font-semibold text-white bg-brand-500">
              {{ initials }}
            </span>
          }
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white break-words min-w-0">{{ displayName }}</h3>
              <app-badge [color]="statusColor" size="sm">{{ statusLabel }}</app-badge>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 break-all">{{ u.mail }}</p>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="shrink-0 flex border-b border-gray-200 dark:border-gray-700 px-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        @for (tab of tabs; track tab.id) {
          <button type="button" (click)="setTab(tab.id)"
            [class.border-brand-500]="activeTab === tab.id"
            [class.text-brand-600]="activeTab === tab.id"
            [class.border-transparent]="activeTab !== tab.id"
            [class.text-gray-500]="activeTab !== tab.id"
            class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap">
            {{ tab.label }}
          </button>
        }
      </div>

      <!-- Content -->
      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-5">

        <!-- ===== INFORMACIÓN ===== -->
        <!--
          Cada dato se pinta como fila ícono + etiqueta + valor, copiando el patrón del detalle
          de Contactos (`contactos/contacto-detail-modal/`), que es el módulo de referencia, y
          reutilizando los mismos SVG que ya usan Contactos y Empresas para cada concepto
          (correo, teléfono, estado, empresa, fecha…). Antes esta pestaña era la única del
          sistema con la información en texto plano.
        -->
        @if (activeTab === 'info') {
          <div class="space-y-6">
            <section>
              <h4 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Información de la cuenta
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Correo electrónico -->
                <div class="flex items-start gap-3 sm:col-span-2">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Correo electrónico</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white break-all">
                      {{ u.mail || '—' }}
                    </p>
                  </div>
                </div>

                <!-- Rol -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Rol</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white break-words">
                      {{ roleName }}
                    </p>
                  </div>
                </div>

                <!-- Estado -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Estado</p>
                    <div class="mt-0.5">
                      <app-badge [color]="statusColor" size="sm">{{ statusLabel }}</app-badge>
                    </div>
                  </div>
                </div>

                <!-- Empresa -->
                <div class="flex items-start gap-3 sm:col-span-2">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Empresa</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white break-words">
                      {{ businessLabel }}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Perfil
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Teléfono -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Teléfono</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ u.phone || '—' }}
                    </p>
                  </div>
                </div>

                <!-- Cargo -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Cargo</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white break-words">
                      {{ u.jobTitle || '—' }}
                    </p>
                  </div>
                </div>

                <!-- País -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">País</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ u.countryAlphabeticCode || '—' }}
                    </p>
                  </div>
                </div>

                <!-- Zona horaria -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Zona horaria</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white break-words">
                      {{ u.timezone || '—' }}
                    </p>
                  </div>
                </div>

                <!-- Descripción -->
                @if (u.bio) {
                  <div class="flex items-start gap-3 sm:col-span-2">
                    <div class="flex-shrink-0 mt-0.5">
                      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z">
                        </path>
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-xs text-gray-500 dark:text-gray-400">Descripción</p>
                      <p class="text-sm font-medium text-gray-900 dark:text-white break-words whitespace-pre-wrap">
                        {{ u.bio }}
                      </p>
                    </div>
                  </div>
                }
              </div>
            </section>

            <section class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Registro
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Fecha de creación -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Creado</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ formatDate(u.createdAt) }}
                    </p>
                  </div>
                </div>

                <!-- Última actualización -->
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
                      </path>
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 dark:text-gray-400">Última actualización</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ formatDate(u.updatedAt) }}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        }

        <!-- ===== LÍNEA DE TIEMPO ===== -->
        @if (activeTab === 'timeline') {
          <!--
            El servidor no conoce /logs/entity/... y se ha caído al endpoint antiguo, que
            responde a otra pregunta. Se avisa SIEMPRE que se muestran esos datos: sin este
            aviso, "creó 12 contactos" se leería como historial de la ficha del usuario.
          -->
          @if (timelineDegradado) {
            <div class="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
              <p class="text-sm font-medium text-amber-800 dark:text-amber-300">
                Mostrando la actividad del usuario, no los cambios sobre su cuenta
              </p>
              <p class="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                Este servidor todavía no publica el historial de cambios de la cuenta, así que se
                muestra lo que este usuario ha hecho en el CRM y sus inicios de sesión. No incluye
                el detalle campo a campo ni quién realizó cada cambio.
              </p>
            </div>
          }
          <app-audit-timeline
            [logs]="timelineLogs"
            [loading]="timelineLoading"
            [error]="timelineError"
            [currentPage]="timelinePage"
            [totalPages]="timelineTotalPages"
            [totalElements]="timelineTotalElements"
            [emptyMessage]="timelineDegradado
              ? 'Este usuario no registra actividad ni inicios de sesión.'
              : 'No hay eventos de auditoría para este usuario.'"
            (pageChange)="cargarTimeline($event)" />
        }
      </div>

      <!--
        Pie: solo lo contextual. Activar, desactivar, restablecer contraseña y eliminar datos
        siguen en la fila de la lista; repetirlas aquí duplicaría acciones de estado en una
        pantalla de consulta.
      -->
      <div class="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <button *appHasPermission="'users:edit'"
          type="button" (click)="onEdit()" [disabled]="!canEdit"
          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          Editar
        </button>
        <button type="button" (click)="onClose()"
          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-brand-500 text-white hover:bg-brand-600">
          Cerrar
        </button>
      </div>
    </div>
  }
</app-modal>
